import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Box,
  Button,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme,
} from '@mui/material';
import FlexBox from 'components/flexbox/FlexBox';
import CreateBoardModal from 'components/modal/crm/CreateBoardModal';
import { H4, H5, H6, Small, Tiny } from 'components/Typography';
import Add from 'icons/Add';
import DeleteIcon from 'icons/DeleteIcon';
import { Edit, GripVertical, Settings } from 'lucide-react';
import { useState } from 'react';
import { customShadows } from 'theme/shadows';

const boardsData = [
  {
    id: 1,
    title: '공지사항',
    path: '/notice',
    views: 14643,
    posts: 143,
    comments: 1115,
    parentId: null,
    order: 0,
  },
  {
    id: 2,
    title: '자유 게시판',
    path: '/free-board',
    views: 14643,
    posts: 143,
    comments: 1115,
    parentId: null,
    order: 1,
  },
  {
    id: 5,
    title: '문서 자료실',
    path: '/free-board/eeeeee',
    views: 14643,
    posts: 143,
    comments: 1115,
    parentId: 2,
    order: 0,
  },
  {
    id: 3,
    title: '자료실',
    path: '/data-room',
    views: 14643,
    posts: 143,
    comments: 1115,
    parentId: null,
    order: 2,
  },
  {
    id: 4,
    title: '꿀정보',
    path: '/honey-information',
    views: 14643,
    posts: 143,
    comments: 1115,
    parentId: null,
    order: 3,
  },
];

const postsData = [
  {
    id: 1,
    title: '안녕하십니까~~',
    author: '사용자112',
    views: 455,
    comments: 4,
    date: '2025-10-14',
  },
  {
    id: 2,
    title: '안녕하십니까~~',
    author: '사용자112',
    views: 455,
    comments: 4,
    date: '2025-10-14',
  },
  {
    id: 3,
    title: '안녕하십니까~~',
    author: '사용자112',
    views: 455,
    comments: 4,
    date: '2025-10-14',
  },
  {
    id: 4,
    title: '안녕하십니까~~',
    author: '사용자112',
    views: 455,
    comments: 4,
    date: '2025-10-14',
  },
  {
    id: 5,
    title: '안녕하십니까~~',
    author: '34222',
    views: 36356,
    comments: 4111,
    date: '2025-10-14',
  },
];

// Sortable Board Item Component
function SortableBoardItem({
  board,
  isSelected,
  isParentOfSelected = false,
  onSelect,
  onAddSubItem,
  theme,
  isSubItem = false,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: board.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        padding: '16px',
        borderRadius: '12px',
        mb: 2,
        ml: isSubItem ? 3 : 0,
        cursor: 'pointer',
        border: isSelected
          ? `1px solid ${theme.palette.primary.mainBlue}`
          : 'none',
        backgroundColor: isSelected
          ? theme.palette.primary.grey10
          : theme.palette.primary.grey800,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: isSelected
            ? theme.palette.primary.grey100
            : theme.palette.primary.grey100,
        },
      }}
    >
      <FlexBox justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1 }} onClick={() => onSelect(board.id)}>
          <H6
            sx={{
              color:
                isSelected || isParentOfSelected
                  ? theme.palette.primary.mainBlue
                  : theme.palette.primary.dark,
              fontWeight: 600,
              mb: 0.5,
            }}
          >
            {board.title}
          </H6>
          <Small
            sx={{
              color: theme.palette.primary.greytxt,
              mb: 1,
              display: 'block',
            }}
          >
            {board.path}
          </Small>
          <FlexBox gap={1}>
            <Tiny
              sx={{
                fontWeight: 500,
                color: theme.palette.primary.dark,
                fontSize: '13px',
              }}
            >
              조회수 {board.views.toLocaleString()}
            </Tiny>
            <Tiny
              sx={{
                fontWeight: 500,
                color: theme.palette.primary.dark,
                fontSize: '13px',
              }}
            >
              게시글 {board.posts}
            </Tiny>
            <Tiny
              sx={{
                fontWeight: 500,
                color: theme.palette.primary.dark,
                fontSize: '13px',
              }}
            >
              댓글 {board.comments.toLocaleString()}
            </Tiny>
          </FlexBox>
        </Box>

        <FlexBox gap={1} alignItems="center">
          {!isSubItem && (
            <Button
              variant="text"
              onClick={e => {
                e.stopPropagation();
                onAddSubItem(board.id);
              }}
              sx={{
                color: theme.palette.primary.greytxt,
                fontSize: '13px',
                fontWeight: 400,
                textTransform: 'none',
                minWidth: 'auto',
                padding: '4px 8px',
                '&:hover': {
                  backgroundColor: 'transparent',
                  color: theme.palette.primary.mainBlue,
                },
              }}
            >
              + 하위 추가
            </Button>
          )}
          <Box
            {...attributes}
            {...listeners}
            sx={{
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              padding: '4px',
              '&:active': {
                cursor: 'grabbing',
              },
            }}
          >
            <GripVertical size={20} color={theme.palette.primary.greytxt} />
          </Box>
        </FlexBox>
      </FlexBox>
    </Card>
  );
}

export default function BoardManagement() {
  const theme = useTheme();
  const [selectedBoardId, setSelectedBoardId] = useState(2);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [parentBoardIdForNewBoard, setParentBoardIdForNewBoard] =
    useState(null);
  const [boards, setBoards] = useState(boardsData);
  const [activeId, setActiveId] = useState(null);

  const selectedBoard = boards.find(board => board.id === selectedBoardId);
  const parentBoard =
    selectedBoard && selectedBoard.parentId !== null
      ? boards.find(board => board.id === selectedBoard.parentId)
      : null;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Organize boards hierarchically
  const organizeBoards = boards => {
    const result = [];
    const parentBoards = boards
      .filter(board => board.parentId === null)
      .sort((a, b) => a.order - b.order);

    parentBoards.forEach(parent => {
      result.push(parent);
      const children = boards
        .filter(board => board.parentId === parent.id)
        .sort((a, b) => a.order - b.order);
      result.push(...children);
    });

    return result;
  };

  const organizedBoards = organizeBoards(boards);

  const handleDragStart = event => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = event => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    setBoards(prevBoards => {
      const oldIndex = prevBoards.findIndex(board => board.id === active.id);
      const overIndex = prevBoards.findIndex(board => board.id === over.id);

      if (oldIndex === -1 || overIndex === -1) return prevBoards;

      const newBoards = [...prevBoards];
      const movedItem = { ...newBoards[oldIndex] };
      const targetBoard = { ...newBoards[overIndex] };

      const movedItemHasChildren = prevBoards.some(
        b => b.parentId === movedItem.id
      );

      newBoards.splice(oldIndex, 1);

      const adjustedOverIndex =
        oldIndex < overIndex ? overIndex - 1 : overIndex;

      const organizedBeforeDrop = organizeBoards(prevBoards);
      const movedOrgIndex = organizedBeforeDrop.findIndex(
        b => b.id === movedItem.id
      );
      const targetOrgIndex = organizedBeforeDrop.findIndex(
        b => b.id === targetBoard.id
      );

      if (targetBoard.parentId !== null) {
        if (movedItemHasChildren) {
          movedItem.parentId = null;
        } else {
          movedItem.parentId = targetBoard.parentId;
        }
      } else {
        const isDroppingRightAfter = targetOrgIndex < movedOrgIndex;

        if (isDroppingRightAfter && !movedItemHasChildren) {
          const targetHasChildren = prevBoards.some(
            b => b.parentId === targetBoard.id
          );
          if (!targetHasChildren) {
            movedItem.parentId = targetBoard.id;
          } else {
            movedItem.parentId = null;
          }
        } else {
          movedItem.parentId = null;
        }
      }

      newBoards.splice(adjustedOverIndex, 0, movedItem);

      const parents = newBoards.filter(b => b.parentId === null);
      parents.forEach((parent, index) => {
        parent.order = index;
        const children = newBoards.filter(b => b.parentId === parent.id);
        children.forEach((child, childIndex) => {
          child.order = childIndex;
        });
      });

      return newBoards;
    });

    setActiveId(null);
  };

  const handleAddSubItem = parentId => {
    setParentBoardIdForNewBoard(parentId);
    setShowCreateModal(true);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Card
          sx={{
            padding: '24px',
            borderRadius: '18px',
            boxShadow: customShadows.card,
            backgroundColor: '#ffffff',
            border: 'none',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <FlexBox alignItems="center" mb={3} gap={1}>
            <Edit
              size={22}
              color={theme.palette.primary.mainBlue}
              strokeWidth={2}
            />
            <H5 sx={{ color: '#151515', fontWeight: 600 }}>게시판 목록</H5>
          </FlexBox>

          <Box sx={{ flex: 1, mb: 3 }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={organizedBoards.map(board => board.id)}
                strategy={verticalListSortingStrategy}
              >
                {organizedBoards.map(board => {
                  const isSelected = selectedBoardId === board.id;
                  const isParentOfSelected =
                    selectedBoard &&
                    selectedBoard.parentId !== null &&
                    selectedBoard.parentId === board.id;

                  return (
                    <SortableBoardItem
                      key={board.id}
                      board={board}
                      isSelected={isSelected}
                      isParentOfSelected={isParentOfSelected}
                      onSelect={setSelectedBoardId}
                      onAddSubItem={handleAddSubItem}
                      theme={theme}
                      isSubItem={board.parentId !== null}
                    />
                  );
                })}
              </SortableContext>
              <DragOverlay>
                {activeId ? (
                  <Card
                    sx={{
                      padding: '16px',
                      borderRadius: '12px',
                      backgroundColor: theme.palette.primary.grey800,
                      boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                    }}
                  >
                    <H6 sx={{ color: theme.palette.primary.dark }}>
                      {boards.find(b => b.id === activeId)?.title}
                    </H6>
                  </Card>
                ) : null}
              </DragOverlay>
            </DndContext>
          </Box>

          <Button
            fullWidth
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setParentBoardIdForNewBoard(null);
              setShowCreateModal(true);
            }}
            sx={{
              backgroundColor: theme.palette.primary.mainBlue,
              color: '#ffffff',
              borderRadius: '12px',
              padding: '12px',
              fontSize: '15px',
              fontWeight: 500,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: theme.palette.primary.mainBlueHover,
                boxShadow: 'none',
              },
            }}
          >
            새 게시판 만들기
          </Button>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card
          sx={{
            padding: '24px',
            borderRadius: '18px',
            boxShadow: customShadows.card,
            backgroundColor: '#ffffff',
            border: 'none',
          }}
        >
          <FlexBox justifyContent="space-between" alignItems="center" mb={1}>
            <H5 sx={{ color: '#151515', fontWeight: 600 }}>
              {parentBoard
                ? `${parentBoard.title} > ${selectedBoard?.title}`
                : selectedBoard?.title}
            </H5>
            <FlexBox gap={1}>
              <Button
                variant="contained"
                startIcon={<Settings size={18} />}
                sx={{
                  backgroundColor: theme.palette.primary.mainBlue,
                  color: '#ffffff',
                  borderRadius: '12px',
                  padding: '4px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.primary.mainBlueHover,
                    boxShadow: 'none',
                  },
                }}
              >
                설정 수정
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon sx={{ fontSize: '16px' }} />}
                sx={{
                  border: 'none',
                  backgroundColor: theme.palette.primary.grey10,
                  color: theme.palette.primary.greytxt,
                  borderRadius: '12px',
                  padding: '4px 16px',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    border: 'none',
                    backgroundColor: '#F5F5F5',
                  },
                }}
              >
                삭제
              </Button>
            </FlexBox>
          </FlexBox>

          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: 'none',
                  border: 'none',
                  backgroundColor: theme.palette.primary.grey800,
                }}
              >
                <Small sx={{ color: '#808187', display: 'block' }}>
                  총 조회수
                </Small>
                <H4 sx={{ color: '#151515', fontWeight: 700 }}>
                  {selectedBoard?.views.toLocaleString()}
                </H4>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: 'none',
                  border: 'none',
                  backgroundColor: theme.palette.primary.grey800,
                }}
              >
                <Small sx={{ color: '#808187', display: 'block' }}>
                  총 게시글 수
                </Small>
                <H4 sx={{ color: '#151515', fontWeight: 700 }}>
                  {selectedBoard?.posts.toLocaleString()}
                </H4>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card
                sx={{
                  padding: '20px',
                  borderRadius: '12px',
                  boxShadow: 'none',
                  border: 'none',
                  backgroundColor: theme.palette.primary.grey800,
                }}
              >
                <Small sx={{ color: '#808187', display: 'block' }}>
                  총 댓글 수
                </Small>
                <H4 sx={{ color: '#151515', fontWeight: 700 }}>
                  {selectedBoard?.comments.toLocaleString()}
                </H4>
              </Card>
            </Grid>
          </Grid>

          <Box>
            <H5 sx={{ color: '#151515', fontWeight: 600, mb: 1 }}>
              게시글 목록
            </H5>
            <Box
              sx={{
                borderRadius: '12px',
                overflow: 'hidden',
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#ffffff' }}>
                    <TableCell
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.primary.greytxt,
                        borderBottom: '1px solid #E8E8E8',
                        padding: '16px',
                      }}
                    >
                      번호
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.primary.greytxt,
                        borderBottom: '1px solid #E8E8E8',
                        padding: '16px',
                      }}
                    >
                      제목
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.primary.greytxt,
                        borderBottom: '1px solid #E8E8E8',
                        padding: '16px',
                      }}
                    >
                      작성자
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.primary.greytxt,
                        borderBottom: '1px solid #E8E8E8',
                        padding: '16px',
                      }}
                    >
                      조회수
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.primary.greytxt,
                        borderBottom: '1px solid #E8E8E8',
                        padding: '16px',
                      }}
                    >
                      댓글
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        color: theme.palette.primary.greytxt,
                        borderBottom: '1px solid #E8E8E8',
                        padding: '16px',
                      }}
                    >
                      작성일
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {postsData.map(post => (
                    <TableRow
                      key={post.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#FAFAFA',
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontSize: '14px',
                          color: '#151515',
                          borderBottom: '1px solid #E8E8E8',
                          padding: '16px',
                        }}
                      >
                        {post.id}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: '14px',
                          color: '#151515',
                          borderBottom: '1px solid #E8E8E8',
                          padding: '16px',
                        }}
                      >
                        {post.title}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '14px',
                          color: '#151515',
                          borderBottom: '1px solid #E8E8E8',
                          padding: '16px',
                        }}
                      >
                        {post.author}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '14px',
                          color: '#151515',
                          borderBottom: '1px solid #E8E8E8',
                          padding: '16px',
                        }}
                      >
                        {post.views.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '14px',
                          color: '#151515',
                          borderBottom: '1px solid #E8E8E8',
                          padding: '16px',
                        }}
                      >
                        {post.comments.toLocaleString()}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontSize: '14px',
                          color: '#151515',
                          borderBottom: '1px solid #E8E8E8',
                          padding: '16px',
                        }}
                      >
                        {post.date}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Card>
      </Grid>

      <CreateBoardModal
        showModal={showCreateModal}
        setShowModal={newValue => {
          setShowCreateModal(newValue);
          if (!newValue) {
            setParentBoardIdForNewBoard(null);
          }
        }}
        parentBoardId={parentBoardIdForNewBoard}
      />
    </Grid>
  );
}
